import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/* Phase 1: the configuration half of the Predictions tab. The evidence half
   (predicted-vs-actual, per-competency error, backtesting) arrives with the
   learned tier — the entries already carry predicted_wait_low/high/source, so
   the history it needs is accumulating from the moment this is switched on. */

const TIERS = [
  { value: 'none',       label: 'Off',                  hint: 'No predictions anywhere. Baseline.' },
  { value: 'flat',       label: 'Flat',                 hint: 'One duration for every student.' },
  { value: 'competency', label: 'Per competency',       hint: 'Your declared minutes per course-competency, falling back to flat.' },
  { value: 'learned',    label: 'Learned',              hint: 'Your own history — this term weighted highest, falling back to declared values when a competency has too little to go on.' },
];

function Hint({ children }) {
  return (
    <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 12, lineHeight: 1.5 }}>
      {children}
    </p>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '14px 16px', flex: '1 1 130px', minWidth: 130,
    }}>
      <div style={{ fontSize: 11, color: 'var(--txt-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || 'var(--txt)', marginTop: 4, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--txt-dim)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Note({ children }) {
  return (
    <div style={{ fontSize: 11, color: 'var(--txt-faint)', marginTop: 6, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export default function PredictionsTab({ session }) {
  const { apiFetch } = useAuth();

  const [cfg,      setCfg]      = useState(null);
  const [exists,   setExists]   = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');

  const [courses,   setCourses]   = useState([]);
  const [durCourse, setDurCourse] = useState('');
  const [durations, setDurations] = useState([]);
  const [durLoading, setDurLoading] = useState(false);
  const [durDirty,  setDurDirty]  = useState(false);

  const [override, setOverride] = useState(null);
  const [preview,  setPreview]  = useState(null);

  const [learned,  setLearned]  = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [accDays,  setAccDays]  = useState(90);

  const [backtest,    setBacktest]    = useState(null);
  const [backtestBusy, setBacktestBusy] = useState(false);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else         { setSuccess(msg); setError(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  /* ── Load ──────────────────────────────────────────────────────────────── */

  // Surfaces failures instead of swallowing them. A silent catch here leaves the
  // tab sitting on "Loading..." forever, which says nothing about whether the
  // API is down, the migrations haven't run, or the role check rejected you.
  const loadConfig = useCallback(async () => {
    try {
      const res = await apiFetch('/api/predictions/config');
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(
          `Could not load prediction settings — HTTP ${res.status}. ` +
          (typeof d.detail === 'string' ? d.detail : 'See the backend log for details.')
        );
        return;
      }
      const { exists: e, ...rest } = await res.json();
      setExists(!!e);
      setCfg(rest);
    } catch (err) {
      setError(`Could not reach the prediction API: ${err.message}`);
    }
  }, [apiFetch]);

  const loadCourses = useCallback(async () => {
    try {
      const res = await apiFetch('/api/courses/mine');
      if (res.ok) setCourses(await res.json());
    } catch { /* ignore */ }
  }, [apiFetch]);

  const loadOverride = useCallback(async () => {
    if (!session?.session_id) { setOverride(null); return; }
    try {
      const res = await apiFetch(`/api/predictions/session/${session.session_id}/override`);
      if (res.ok) setOverride(await res.json());
    } catch { /* ignore */ }
  }, [apiFetch, session?.session_id]);

  const loadPreview = useCallback(async () => {
    if (!session?.session_id) { setPreview(null); return; }
    try {
      const res = await apiFetch(`/api/predictions/session/${session.session_id}/preview`);
      if (res.ok) setPreview(await res.json());
    } catch { /* ignore */ }
  }, [apiFetch, session?.session_id]);

  const loadAccuracy = useCallback(async (days) => {
    try {
      const res = await apiFetch(`/api/predictions/accuracy?days=${days}`);
      if (res.ok) setAccuracy(await res.json());
    } catch { /* ignore */ }
  }, [apiFetch]);

  useEffect(() => { loadConfig(); loadCourses(); }, [loadConfig, loadCourses]);
  useEffect(() => { loadAccuracy(accDays); }, [loadAccuracy, accDays]);
  useEffect(() => { loadOverride(); loadPreview(); }, [loadOverride, loadPreview]);

  // The preview is the instructor's own view, so it can refresh briskly — the
  // deliberately-coarse refresh_seconds knob governs what STUDENTS see, not this.
  useEffect(() => {
    if (!session?.session_id) return;
    const id = setInterval(loadPreview, 15000);
    return () => clearInterval(id);
  }, [session?.session_id, loadPreview]);

  const runBacktest = async () => {
    if (!cfg) return;
    setBacktestBusy(true);
    setBacktest(null);
    try {
      const res = await apiFetch('/api/predictions/backtest', {
        method: 'POST',
        body: JSON.stringify({ settings: cfg, days: accDays }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) setBacktest(d);
      else flash(typeof d.detail === 'string' ? d.detail : 'Backtest failed.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBacktestBusy(false); }
  };

  const loadLearned = async (courseId) => {
    if (!courseId) { setLearned(null); return; }
    try {
      const res = await apiFetch(`/api/predictions/learned/${courseId}`);
      setLearned(res.ok ? await res.json() : null);
    } catch { setLearned(null); }
  };

  const loadDurations = async (courseId) => {
    if (!courseId) { setDurations([]); return; }
    setDurLoading(true);
    setDurDirty(false);
    try {
      const res = await apiFetch(`/api/predictions/durations/${courseId}`);
      setDurations(res.ok ? await res.json() : []);
    } catch { setDurations([]); }
    finally { setDurLoading(false); }
  };

  /* ── Save ──────────────────────────────────────────────────────────────── */

  const set = (patch) => setCfg(c => ({ ...c, ...patch }));

  const saveConfig = async (overrides = {}) => {
    if (!cfg) return;
    setBusy(true);
    try {
      const body = { ...cfg, ...overrides };
      const res = await apiFetch('/api/predictions/config', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        const { exists: e, ...rest } = d;
        setExists(true);
        setCfg(rest);
        flash('Prediction settings saved.');
        loadPreview();
        loadOverride();
      } else {
        flash(typeof d.detail === 'string' ? d.detail : 'Could not save prediction settings.', true);
      }
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  const saveDurations = async () => {
    if (!durCourse) return;
    setBusy(true);
    try {
      const res = await apiFetch('/api/predictions/durations', {
        method: 'PUT',
        body: JSON.stringify({
          course_id: Number(durCourse),
          items: durations.map(d => ({
            course_competency_id: d.course_competency_id,
            minutes: d.minutes === '' || d.minutes == null ? null : Number(d.minutes),
          })),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { flash(d.message || 'Durations saved.'); setDurDirty(false); loadPreview(); }
      else flash(typeof d.detail === 'string' ? d.detail : 'Could not save durations.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  const saveOverride = async (patch) => {
    if (!session?.session_id) return;
    setBusy(true);
    try {
      const body = {
        prediction_tier:            override?.prediction_tier ?? null,
        prediction_student_visible: override?.prediction_student_visible ?? null,
        ...patch,
      };
      const res = await apiFetch(`/api/predictions/session/${session.session_id}/override`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (res.ok) { flash('Session override updated.'); await loadOverride(); loadPreview(); }
      else flash('Could not update the session override.', true);
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  if (!cfg) {
    return (
      <div className={styles.panel}>
        {error
          ? <div className={styles.error}>{error}</div>
          : <p className={styles.empty}>Loading…</p>}
      </div>
    );
  }

  const tierOff = cfg.tier === 'none';
  const effTier = override?.effective_tier ?? cfg.tier;

  return (
    <div className={styles.panel}>
      {success && <div className={styles.success}>{success}</div>}
      {error   && <div className={styles.error}>{error}</div>}

      {/* ── What's in force right now ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Wait Predictions</h3>
        <Hint>
          Every tier answers one question — how long a student takes once they sit
          down. The queue arithmetic is the same either way, so switching tiers
          changes the estimate, never the maths.
        </Hint>

        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12,
          padding: '10px 12px', borderRadius: 6, marginBottom: 4,
          background: effTier === 'none' ? 'var(--bg-card)' : 'rgba(48,164,108,0.1)',
          border: `1px solid ${effTier === 'none' ? 'var(--border)' : 'rgba(48,164,108,0.3)'}`,
          color: effTier === 'none' ? 'var(--txt-dim)' : 'var(--green, #30a46c)',
        }}>
          <span>
            {effTier === 'none'
              ? 'Predictions are off — students and you both see nothing.'
              : `In force: ${TIERS.find(t => t.value === effTier)?.label ?? effTier}`}
          </span>
          {effTier !== 'none' && (
            <span style={{ marginLeft: 'auto' }}>
              {(override?.effective_student_visible ?? cfg.student_visible)
                ? 'Students can see them'
                : 'You only — students see nothing'}
            </span>
          )}
        </div>
        {!exists && (
          <Note>
            Nothing saved yet, so the feature is off by absence. Saving once
            creates your settings row; the tier below still controls whether
            anything is shown.
          </Note>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Tier ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Estimator</h3>
        <div className={styles.field}>
          <label>Tier</label>
          <select value={cfg.tier} onChange={e => set({ tier: e.target.value })}>
            {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <Note>{TIERS.find(t => t.value === cfg.tier)?.hint}</Note>

        {(cfg.tier === 'flat' || cfg.tier === 'competency' || cfg.tier === 'learned') && (
          <div className={styles.row} style={{ marginTop: 12 }}>
            <div className={styles.field}>
              <label>Flat minutes</label>
              <input type="number" min="1" max="240" value={cfg.flat_minutes}
                onChange={e => set({ flat_minutes: Number(e.target.value) })} />
            </div>
          </div>
        )}
        {cfg.tier !== 'flat' && cfg.tier !== 'none' && (
          <Note>
            Flat is still the last link in the chain: a student who joins for
            Questions, or picks a competency with no declared value, uses this.
          </Note>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '14px 0 4px' }}>
          <input type="checkbox" style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={cfg.student_visible}
            onChange={e => set({ student_visible: e.target.checked })} />
          Show predictions to students
        </label>
        <Note>
          You always see predictions in the panel below. Leave this off until the
          numbers look right — it's the whole reason the toggle exists.
        </Note>

        <button className="btn-primary" onClick={() => saveConfig()} disabled={busy}
          style={{ marginTop: 14 }}>
          Save
        </button>
      </section>

      <div className={styles.divider} />

      {/* ── Presentation ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>How the estimate reads</h3>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>Conservatism %</label>
            <input type="number" min="0" max="200" value={cfg.conservatism_pct}
              onChange={e => set({ conservatism_pct: Number(e.target.value) })} />
          </div>
          <div className={styles.field}>
            <label>Minimum band (min)</label>
            <input type="number" min="0" max="60" value={cfg.min_band_minutes}
              onChange={e => set({ min_band_minutes: Number(e.target.value) })} />
          </div>
          <div className={styles.field}>
            <label>Refresh (sec)</label>
            <input type="number" min="60" max="900" step="30" value={cfg.refresh_seconds}
              onChange={e => set({ refresh_seconds: Number(e.target.value) })} />
          </div>
          <div className={styles.field}>
            <label>Range width</label>
            <select value={cfg.range_mode} onChange={e => set({ range_mode: e.target.value })}>
              <option value="iqr">IQR — tighter, more actionable</option>
              <option value="wide">Wide — 10th–90th</option>
            </select>
          </div>
        </div>

        <Note>
          <strong>Conservatism</strong> skews the upper bound only. Overestimating
          isn't free — inflate it and students leave who would have been seen.
          <br />
          <strong>Minimum band</strong> stops a short queue collapsing to something
          like "3–4 minutes", which reads as a promise. At {cfg.min_band_minutes || 0}
          {' '}the narrowest a student can ever see is a {cfg.min_band_minutes || 0}-minute
          spread; the widening is centred so it doesn't stack on top of conservatism.
          Set 0 to turn the floor off.
          <br />
          <strong>Refresh</strong> is deliberately coarse so the estimate reads as
          periodic rather than a countdown that visibly jumps backwards on a reorder.
          <br />
          Conservatism and range width both widen what students see — worth
          checking they don't compound into something uselessly vague.
        </Note>

        <button className="btn-primary" onClick={() => saveConfig()} disabled={busy}
          style={{ marginTop: 14 }}>
          Save
        </button>
      </section>

      <div className={styles.divider} />

      {/* ── Correctness ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Correctness</h3>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>Overrun floor (min)</label>
            <input type="number" min="0" max="60" value={cfg.overrun_floor_minutes}
              onChange={e => set({ overrun_floor_minutes: Number(e.target.value) })} />
          </div>
        </div>
        <Note>
          Applies to whoever is in the room. 0 allows the overrun, so their
          remaining time can reach zero. Raise it if "0 more minutes" reads badly.
        </Note>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '14px 0 4px' }}>
          <input type="checkbox" style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={cfg.count_test_students_live}
            onChange={e => set({ count_test_students_live: e.target.checked })} />
          Count test students in the live wait
        </label>
        <Note>
          Off by default — a test student holds a position but takes none of your
          time, so counting one overstates the wait for every real student behind
          it. Turn it on to demo predictions with a queue of test students, which
          otherwise shows nothing at all.
          <br />
          This only affects the number on screen. Test students are never learned
          from and never appear in accuracy, backtesting or analytics, whatever
          this is set to.
        </Note>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '14px 0 4px' }}>
          <input type="checkbox" style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
            checked={cfg.suppress_during_pooling}
            onChange={e => set({ suppress_during_pooling: e.target.checked })} />
          Suppress predictions while a pooled session is still pooling
        </label>
        <Note>Position before the shuffle is meaningless, so a prediction from it would be too.</Note>

        <div style={{ marginTop: 18, opacity: 0.75 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt-faint)',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Learned tier (phase 2)
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Min sample (min)</label>
              <input type="number" min="0" max="60" value={cfg.min_sample_minutes}
                onChange={e => set({ min_sample_minutes: Number(e.target.value) })} />
            </div>
            <div className={styles.field}>
              <label>Max sample (min)</label>
              <input type="number" min="1" max="480" value={cfg.max_sample_minutes}
                onChange={e => set({ max_sample_minutes: Number(e.target.value) })} />
            </div>
            <div className={styles.field}>
              <label>Min samples</label>
              <input type="number" min="1" max="100" value={cfg.min_samples}
                onChange={e => set({ min_samples: Number(e.target.value) })} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '12px 0 4px' }}>
            <input type="checkbox" style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }}
              checked={cfg.use_time_of_day}
              onChange={e => set({ use_time_of_day: e.target.checked })} />
            Split this term's history into morning and afternoon
          </label>
          <Note>
            Durations outside the sample range are discarded — too long is usually
            a forgotten "Mark Complete", too short a mis-click.
            <br />
            Two buckets rather than hour-of-day: an hourly slice would almost
            never reach {cfg.min_samples} samples for a single competency. The
            split is only used when the bucket has enough on its own, so it can
            sharpen an estimate but never costs you a signal.
          </Note>
        </div>

        <button className="btn-primary" onClick={() => saveConfig()} disabled={busy}
          style={{ marginTop: 14 }}>
          Save
        </button>
      </section>

      <div className={styles.divider} />

      {/* ── Tier 2 grid ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Declared Durations</h3>
        <Hint>
          Your minutes for each competency, per course — the same competency can
          be assessed at different depth in different courses, so they're kept
          separate. Blank falls back to flat.
        </Hint>

        <div className={styles.field}>
          <label>Course</label>
          <select value={durCourse}
            onChange={e => {
              setDurCourse(e.target.value);
              loadDurations(e.target.value);
              loadLearned(e.target.value);
            }}>
            <option value="">Select course…</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.course_code} — {c.course_name}</option>
            ))}
          </select>
        </div>

        {durLoading && <p style={{ fontSize: 13, color: 'var(--txt-dim)', marginTop: 10 }}>Loading…</p>}

        {!durLoading && durCourse && durations.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--txt-faint)', marginTop: 8 }}>
            No competencies for this course yet. Import a list in Manage first.
          </p>
        )}

        {!durLoading && durations.length > 0 && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Competency</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)', width: 120 }}>Minutes</th>
                </tr>
              </thead>
              <tbody>
                {durations.map((d, i) => (
                  <tr key={d.course_competency_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', color: 'var(--txt)' }}>{d.name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      <input
                        type="number" min="1" max="240"
                        value={d.minutes ?? ''}
                        placeholder={String(cfg.flat_minutes)}
                        onChange={e => {
                          const v = e.target.value;
                          setDurations(list => list.map((x, j) =>
                            j === i ? { ...x, minutes: v === '' ? null : Number(v) } : x));
                          setDurDirty(true);
                        }}
                        style={{ width: 90, textAlign: 'right' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Note>
              Greyed placeholders show the flat fallback that would be used
              instead. A student who joins for Questions always uses flat —
              there's no competency to declare against.
            </Note>
            <button className="btn-primary" onClick={saveDurations} disabled={busy || !durDirty}
              style={{ marginTop: 14 }}>
              {durDirty ? 'Save Durations' : 'Saved'}
            </button>
          </>
        )}

        {learned?.configured && learned.rows.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt-faint)',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              What the learned tier has to work with
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Competency</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Samples</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Median</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                    fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Spread</th>
                </tr>
              </thead>
              <tbody>
                {learned.rows.map(r => (
                  <tr key={r.course_competency_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', color: 'var(--txt)' }}>{r.name}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right',
                      color: r.trusted ? 'var(--txt-dim)' : 'var(--amber)' }}>
                      {r.samples}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right',
                      color: r.trusted ? 'var(--gold)' : 'var(--txt-faint)' }}>
                      {r.trusted ? `${r.median} min` : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>
                      {r.trusted ? `${r.low}–${r.high}` : 'falling back'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Note>
              Anything under {learned.min_samples} samples is ignored by the
              learned tier and drops to your declared value, then to flat. That's
              deliberate — a median from two data points is noise wearing a
              number's clothes.
            </Note>
          </div>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Evidence ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Accuracy</h3>
        <Hint>
          Every prediction is stamped on the entry when the student joins, so this
          compares what they were told against the wait that actually happened.
          Being anywhere inside the band counts as right — that's what a range is
          for.
        </Hint>

        <div className={styles.field}>
          <label>Window</label>
          <select value={accDays} onChange={e => setAccDays(Number(e.target.value))}>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>

        {!accuracy || accuracy.n === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--txt-faint)', marginTop: 10 }}>
            {accuracy?.message || 'No predictions have been matched to an outcome yet.'}
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <Stat label="Within band" value={`${accuracy.within_pct}%`}
                sub={`${accuracy.within_band} of ${accuracy.n}`} accent="var(--gold)" />
              <Stat label="Median miss" value={`${accuracy.median_error} min`}
                sub="0 = inside the band" />
              <Stat label="Ran late" value={accuracy.under_count}
                sub="waited longer than promised" />
              <Stat label="Ran early" value={accuracy.over_count}
                sub="seen sooner than promised" />
            </div>

            <Note>
              Late misses mean the estimate is optimistic — raise conservatism or
              the declared durations. Early misses aren't free either: a student
              told 40 minutes who'd have waited 15 may just leave.
            </Note>

            {accuracy.by_source?.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt-faint)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  By estimator
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Source</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>n</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Median miss</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Worst</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accuracy.by_source.map(r => (
                      <tr key={r.source} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--txt)', fontSize: 12 }}>{r.source}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>{r.n}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gold)' }}>{r.median_error} min</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>{r.max_error} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Note>
                  This is the comparison worth acting on: if "competency" is
                  beating "learned(...)", the history isn't ready yet and the
                  declared values are doing better work.
                </Note>
              </div>
            )}
          </>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── Backtest ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Backtest</h3>
        <Hint>
          Replays your stored history against the settings currently on screen —
          including edits you haven't saved. Nothing is written, so this is safe
          to run repeatedly while you tune.
        </Hint>

        <button className="btn-primary" onClick={runBacktest} disabled={backtestBusy}>
          {backtestBusy ? 'Replaying…' : `Replay last ${accDays} days`}
        </button>

        {backtest && backtest.replayable === 0 && (
          <p style={{ fontSize: 13, color: 'var(--txt-faint)', marginTop: 12 }}>
            {backtest.message || 'Nothing replayable in this window yet.'} Entries
            only became replayable once the queue state started being stored at
            join, so this fills in from here forward.
          </p>
        )}

        {backtest && backtest.replayable > 0 && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
              <Stat label="These settings"
                value={backtest.candidate.median_error != null
                  ? `${backtest.candidate.median_error} min` : '—'}
                sub={`median miss · ${backtest.candidate.within_pct ?? 0}% in band`}
                accent="var(--gold)" />
              <Stat label="What you ran"
                value={backtest.actual.median_error != null
                  ? `${backtest.actual.median_error} min` : '—'}
                sub={`median miss · ${backtest.actual.within_pct ?? 0}% in band`} />
              <Stat label="Replayed" value={backtest.replayable}
                sub={`of ${backtest.n} entries`} />
              {backtest.candidate.silent > 0 && (
                <Stat label="Said nothing" value={backtest.candidate.silent}
                  sub="suppressed or not enough to say" />
              )}
            </div>

            <div style={{ fontSize: 13, color: 'var(--txt)', marginTop: 14, lineHeight: 1.6 }}>
              {backtest.candidate.median_error == null ? (
                <>These settings would have stayed silent across the whole window.</>
              ) : backtest.actual.median_error == null ? (
                <>
                  These settings would have made {backtest.candidate.scored} predictions
                  where you previously made none.
                </>
              ) : backtest.candidate.median_error < backtest.actual.median_error ? (
                <>
                  These settings would have been off by a median of{' '}
                  <strong style={{ color: 'var(--gold)' }}>{backtest.candidate.median_error} min</strong>
                  {' '}against {backtest.actual.median_error} min for what you actually ran.
                </>
              ) : backtest.candidate.median_error > backtest.actual.median_error ? (
                <>
                  These settings would have been <strong>worse</strong> — a median miss of{' '}
                  {backtest.candidate.median_error} min against {backtest.actual.median_error} min
                  for what you actually ran.
                </>
              ) : (
                <>No measurable difference from what you actually ran.</>
              )}
              <div style={{ fontSize: 12, color: 'var(--txt-dim)', marginTop: 6 }}>
                Candidate misses: {backtest.candidate.late} late,{' '}
                {backtest.candidate.early} early · worst {backtest.candidate.max_error ?? '—'} min
              </div>
            </div>

            <Note>
              The learned tier is cut off at each student's join time during the
              replay, so it only sees history that existed then — otherwise it
              would be graded on knowledge it didn't have and every candidate
              would look excellent.
            </Note>
          </>
        )}
      </section>

      <div className={styles.divider} />

      {/* ── This session ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>This Session</h3>

        {!session ? (
          <p className={styles.empty}>No active session. Open one to override settings for it.</p>
        ) : (
          <>
            <Hint>
              A one-off override for this session only — a review day or exam week
              can run a different estimator, or hide predictions, without touching
              your defaults. Clearing falls back to the settings above.
            </Hint>

            <div className={styles.row}>
              <div className={styles.field}>
                <label>Tier for this session</label>
                <select
                  value={override?.prediction_tier ?? ''}
                  onChange={e => saveOverride({ prediction_tier: e.target.value || null })}>
                  <option value="">Use my default ({cfg.tier})</option>
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.field}>
                <label>Student visibility</label>
                <select
                  value={override?.prediction_student_visible == null
                    ? '' : String(override.prediction_student_visible)}
                  onChange={e => saveOverride({
                    prediction_student_visible: e.target.value === '' ? null : e.target.value === 'true',
                  })}>
                  <option value="">Use my default ({cfg.student_visible ? 'visible' : 'hidden'})</option>
                  <option value="true">Visible to students</option>
                  <option value="false">Hidden from students</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt-faint)',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                Live queue — what the estimator says right now
              </div>

              {!preview || preview.entries.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--txt-faint)' }}>
                  {preview && preview.tier === 'none'
                    ? 'Predictions are off for this session.'
                    : 'Nobody waiting.'}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)', width: 40 }}>#</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Student</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Now</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--txt-dim)',
                        fontWeight: 600, borderBottom: '1px solid var(--border)' }}>At join</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.map(e => (
                      <tr key={e.entry_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--txt-faint)' }}>{e.position}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--txt)' }}>{e.display_name}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gold)' }}>
                          {e.prediction
                            ? `${e.prediction.low_minutes}–${e.prediction.high_minutes} min`
                            : '—'}
                          {e.prediction?.source && (
                            <div style={{ fontSize: 10, color: 'var(--txt-faint)' }}>
                              {e.prediction.source}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--txt-dim)' }}>
                          {e.predicted_at_join
                            ? `${e.predicted_at_join.low_minutes}–${e.predicted_at_join.high_minutes} min`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {preview?.truncated && (
                <Note>Showing the first 25 waiting students.</Note>
              )}
              {preview && preview.entries.length > 0 && (
                <Note>
                  "At join" is what that student was actually told. The gap between
                  the two columns is the drift the accuracy analytics will measure
                  once the learned tier lands.
                </Note>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
